import { CREATE_SITE_APPLICATION } from '@/mutations';
import { VALIDATE_INVITATION } from '@/queries';
import { Box, Button, Container, Typography } from '@mui/material';
import { FormikValues } from 'formik';
import { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { uiOnlyFields } from '@/constants/formInitConstants';
import NonInviteFlowMainScreen from '@/components/NonInviteFlow/Main';
import InviteFlowMainScreen from '@/components/InviteFlow/Main';
import { useRouter } from 'next/router';
import { ArrowForwardRounded } from '@mui/icons-material';
import LinearLoader from '@/components/LinearLoader';
import {
  CreateSiteApplicationMutation,
  CreateSiteApplicationMutationVariables,
  DuplicateType,
  SiteApplicationInput,
  ValidateSiteVaultInvitationQuery,
  ValidateSiteVaultInvitationQueryVariables,
} from '@/gql/graphql';
import { formatSiteName } from '@/utils';

const InvalidInvite = () => {
  const router = useRouter();
  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 132px)',
        }}>
        <Box textAlign="center">
          <Typography variant="h3">This invitation is no longer valid</Typography>
          <Button
            sx={{ marginTop: 4 }}
            onClick={() => void router.push(process.env.NEXT_PUBLIC_SITES_VEEVA_URL as string)}
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardRounded />}>
            Go to sites.veeva.com
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

const HomePage = () => {
  const router = useRouter();
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [isInviteFlow, setIsInviteFlow] = useState(false);

  const [createApplication, { data, loading, error }] = useMutation<
    CreateSiteApplicationMutation,
    CreateSiteApplicationMutationVariables
  >(CREATE_SITE_APPLICATION);

  const [
    validateInvitation,
    { called: validateInvitationCalled, data: validateInvitationData, loading: validating },
  ] = useLazyQuery<ValidateSiteVaultInvitationQuery, ValidateSiteVaultInvitationQueryVariables>(
    VALIDATE_INVITATION
  );

  const haveSiteVaultFlow = Boolean(
    router &&
      router.query &&
      router.query.alreadyHaveSiteVault &&
      router.query.alreadyHaveSiteVault === 'true'
  );

  useEffect(() => {
    if (loading || !data) return;
    if (data.createSiteApplication) {
      window.scrollTo(0, 0);
      setIsConfirmation(true);
    }
  }, [loading, data]);

  useEffect(() => {
    if (!router.isReady) return;
    if (router.query.code) {
      setIsInviteFlow(true);
      void validateInvitation({ variables: { id: router.query.code as string } });
    }
  }, [router.isReady]);

  const handleSubmit = (
    values: FormikValues,
    duplicateUsn?: string,
    callback: VoidFunction = () => {}
  ) => {
    const vals = { ...values } as SiteApplicationInput;
    const fieldsToRemove = Object.keys(uiOnlyFields);
    fieldsToRemove.forEach((field) => delete vals[field]);
    if (isInviteFlow) {
      vals.inviteId = router.query.code as string;
    }
    if (duplicateUsn) {
      vals.duplicateUsn = duplicateUsn;
    }
    if (haveSiteVaultFlow) {
      vals.duplicateType = DuplicateType.Aware;
    }
    vals.name = formatSiteName(
      vals.name,
      vals.organization,
      vals.siteType,
      vals.hasMultiLocations,
      vals.country,
      vals.city,
      vals.stateProvince
    );
    void createApplication({
      variables: {
        application: vals,
      },
      onCompleted: callback,
    });
  };

  const graphQLErrors = error && error.graphQLErrors;

  if (
    !router.isReady ||
    (router.isReady && router.query.code && !validateInvitationCalled) ||
    validating
  ) {
    return <LinearLoader />;
  }

  if (
    isInviteFlow &&
    (validateInvitationData?.validateSiteVaultInvitation?.isClaimed ||
      !validateInvitationData?.validateSiteVaultInvitation?.isValid)
  ) {
    return <InvalidInvite />;
  }

  if (
    isInviteFlow &&
    validateInvitationData?.validateSiteVaultInvitation?.isValid &&
    !validateInvitationData.validateSiteVaultInvitation.isClaimed
  ) {
    return (
      <Container>
        <InviteFlowMainScreen
          graphQLErrors={graphQLErrors}
          onSubmit={handleSubmit}
          loading={loading}
          isConfirmation={isConfirmation}
          invitationData={validateInvitationData.validateSiteVaultInvitation}
          applicationId={data?.createSiteApplication?.id}
          haveSiteVaultFlow={haveSiteVaultFlow}
        />
      </Container>
    );
  }

  return (
    <Container>
      <NonInviteFlowMainScreen
        graphQLErrors={graphQLErrors}
        onSubmit={handleSubmit}
        loading={loading}
        isConfirmation={isConfirmation}
        applicationId={data?.createSiteApplication?.id}
        haveSiteVaultFlow={haveSiteVaultFlow}
      />
    </Container>
  );
};

export default HomePage;
